import {
    useDestroyCommissionGroupMutation,
    useGetCommissionGroupsQuery,
    useUpdateCommissionGroupMutation,
    useExportCommissionGroupsMutation,
} from "api/TenantSlug/CommissionGroup/hooks";
import { AddCommissionGroupDrawer } from "components/Drawers/AddCommissionGroupDrawer/AddCommissionGroupDrawer";
import { ShortenedIdWithCopy } from "components/UI/ShortenedIdWithCopy";
import { Table } from "components/UI/Table";
import { exportAtom } from "components/UI/Table/atoms/exportAtom";
import { tableAtom } from "components/UI/Table/atoms/tableAtom";
import { DEFAULT_PAGE_NUMBER, DEFAULT_PAGE_SIZE } from "components/UI/Table/atoms/tableAtom";
import { FILTER_TYPES } from "components/UI/Table/constants/filter.ts";
import { useTable } from "components/UI/Table/hooks/useTable";
import { useTableDataApiOperations } from "components/UI/Table/hooks/useTableDataApiOperations";
import { PageSize, TableColumnType } from "components/UI/Table/types/table";
import { ColumnHeaderTitle } from "components/UI/Table/UI/ColumnHeaderTitle";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { usePermission } from "hooks/usePermissions";
import { useSmartRowHandlers } from "hooks/useTableRowHandlers";
import { COMMISSION_GROUP_USER_GROUP } from "localConstants";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useRecoilValue } from "recoil";
import { PermissionEnum } from "types/enums";
import { DrawerId, TableId } from "types/models";

import { userGroup } from "./temp";

import CommissionGroupDataOptional = App.Data.CommissionGroupDataOptional;
import GetCommissionsGroupsResponse = App.Http.Responses.CommissionGroup.GetCommissionGroupsResponse;
import GetCommissionGroupResponse = App.Http.Responses.CommissionGroup.GetCommissionGroupResponse;

const defaultTableData: GetCommissionsGroupsResponse = {
    commissionGroups: [],
    pagination: {
        total: 0,
        currentPage: 1,
        perPage: PageSize.Ten,
        totalPages: 1,
        previousPage: 1,
        nextPage: 1,
        from: 1,
        to: 1,
        maxResults: PageSize.OneThousand,
    },
};

export const CommissionWithGroupsTable = () => {
    const tableId = TableId.CommissionGroupsTable;
    const tableAtomInstance = tableAtom<CommissionGroupDataOptional>();
    const tableState = useRecoilValue(tableAtomInstance(tableId));
    const { t } = usePaldockTranslation("tables.commission-group");
    const { t: tG } = usePaldockTranslation("tables.general-columns");
    const { t: tU } = usePaldockTranslation("tables.ui");
    const { t: tGeneral } = usePaldockTranslation("general");
    const permissionsUtils = usePermission();
    const tableUtils = useTable<CommissionGroupDataOptional>(tableId);

    const request = {
        filter: {
            filter: {
                ...(tableState.filter?.filter || {}),
            },
            fields: { commission_groups: "title,description" },
            sort: tableState?.filter?.sort ?? "title",
        },
        page: {
            number: tableState.page?.number ?? DEFAULT_PAGE_NUMBER,
            size: tableState.page?.size ?? DEFAULT_PAGE_SIZE,
        },
    };

    const fetchQuery = useGetCommissionGroupsQuery(request, { enabled: false });
    const destroyMutation = useDestroyCommissionGroupMutation();
    const updateMutation = useUpdateCommissionGroupMutation(tableState.editingRowId || "");
    const getExportMutation = useExportCommissionGroupsMutation();
    const exportState = useRecoilValue(exportAtom(tableId));

    const tableDataUtils = useTableDataApiOperations<
        GetCommissionsGroupsResponse,
        GetCommissionGroupResponse,
        CommissionGroupDataOptional,
        App.Http.Requests.BaseExportRequest
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
    >(tableId, fetchQuery, destroyMutation, updateMutation, getExportMutation);

    const [dataSource, setDataSource] = useState<CommissionGroupDataOptional[]>([userGroup]);

    useEffect(() => {
        if (fetchQuery.data?.commissionGroups) {
            setDataSource([userGroup, ...fetchQuery.data.commissionGroups]);
        }
    }, [fetchQuery.data]);

    const handleDelete = async (record: CommissionGroupDataOptional) => {
        if (record.id === userGroup.id) {
            setDataSource((ds) => ds.filter((item) => item.id !== userGroup.id));
            toast.success(tU("messages.delete_success"));
        } else {
            await tableDataUtils.deleteTableData(record);
        }
        await tableDataUtils.refetchData();
    };

    const handleUpdate = async (record: CommissionGroupDataOptional) => {
        if (record.id === userGroup.id) {
            setDataSource((ds) => ds.map((item) => (item.id === userGroup.id ? { ...item, ...record } : item)));
            toast.success(tU("messages.update_success"));
        } else {
            await tableDataUtils.editTableData(record);
        }
        await tableDataUtils.refetchData();
    };

    const columns: TableColumnType<CommissionGroupDataOptional>[] = [
        {
            title: <ColumnHeaderTitle columnTitle={tG("title")} />,
            dataIndex: "title",
            key: "title",
            inputType: "text",
            render: (title, record) =>
                record.id === COMMISSION_GROUP_USER_GROUP ? tGeneral("default_user_group") : title,
            sorter: true,
            defaultSorter: true,
            ellipsis: true,
            width: 150,
            filterable: true,
            filterType: FILTER_TYPES.string,
        },
        {
            title: <ColumnHeaderTitle columnTitle={tG("description")} />,
            dataIndex: "description",
            key: "description",
            inputType: "text",
            sorter: true,
            ellipsis: true,
            width: 200,
            filterable: true,
            filterType: FILTER_TYPES.string,
        },
        ...(permissionsUtils.hasPermission(PermissionEnum.CommissionGroupViewForeign)
            ? [
                  {
                      title: <ColumnHeaderTitle columnTitle={t("columns.partners")} />,
                      dataIndex: "number_of_partners",
                      key: "number_of_partners",
                      render: (number_of_partners: number, record: CommissionGroupDataOptional) =>
                          record.id === COMMISSION_GROUP_USER_GROUP ? "-" : number_of_partners,
                      //sorter: true,
                      ellipsis: true,
                      width: 100,
                  },
              ]
            : []),
        {
            title: <ColumnHeaderTitle columnTitle={tG("id")} />,
            dataIndex: "id",
            key: "id",
            inputType: "text",
            render: (id) => <ShortenedIdWithCopy id={id} />,
            sorter: true,
            ellipsis: true,
            width: 160,
            filterable: true,
            filterType: FILTER_TYPES.string,
        },
    ];

    const { getRowHandlers, getRowClassName } = useSmartRowHandlers<CommissionGroupDataOptional>({
        entityName: "commissionGroup",
        detailPermission: PermissionEnum.CommissionGroupView,
        detailUrl: `/$tenantSlug/affiliate/commission/:id`,
    });

    return (
        <>
            {permissionsUtils.hasPermission(PermissionEnum.CommissionGroupCreate) && (
                <AddCommissionGroupDrawer onSubmit={() => tableDataUtils.refetchData()} />
            )}

            <Table<GetCommissionsGroupsResponse, CommissionGroupDataOptional>
                tableId={tableId}
                data={tableDataUtils.tableData ?? defaultTableData}
                dataSource={dataSource}
                columnConfig={columns}
                loading={fetchQuery.isLoading || fetchQuery.isFetching || !fetchQuery.data?.commissionGroups}
                defaultDelete={handleDelete}
                defaultOnUpdate={handleUpdate}
                addEntityData={{
                    drawerId: DrawerId.AddCommissionGroup,
                    permission: PermissionEnum.CommissionGroupCreate,
                }}
                onRefresh={tableDataUtils.refetchData}
                showRefetchButton
                pagination={{
                    showSizeChanger: true,
                    current: tableDataUtils.tableData?.pagination?.currentPage ?? 1,
                    pageSize: tableState.page?.size ?? PageSize.Ten,
                    total: (tableDataUtils.tableData?.pagination?.total || 0) + 1,
                    pageSizeOptions: [PageSize.Five, PageSize.Ten, PageSize.Thirty, PageSize.OneHundred],
                    onChange: (page, size) => tableUtils.savePage({ number: page, size }),
                }}
                rowKey={(record) => record.id}
                onRow={(record, index) => getRowHandlers(record, index)}
                rowClassName={(_, index) => getRowClassName(index)}
                onExport={() =>
                    tableDataUtils.exportData({
                        extension: exportState.extension,
                        headers: exportState.headers,
                        export_all: exportState.range === "all",
                        page: request.page,
                        filter: request.filter,
                        delimiter: exportState.delimiter,
                    })
                }
                enableFilters={true}
                searchDataIndex="title"
                hideActionsColumn={true}
                autoScrollWidth={true}
            />
        </>
    );
};
